import React, { useEffect } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { RootState } from '@/redux/store';
import {
    useCreateBusinessUnitMutation,
    useUpdateBusinessUnitMutation,
} from '@/services/businessUnitApi';
import { BusinessUnit } from '@/types/system-setting/business-unit';
import { FormProps } from '@/types';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FloatingLabelInput } from '@/components/ui/floatinglable/floating';
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { useGetCategoriesQuery } from '@/services/categoryApi';

const BusinessUnitForm = ({
    isOpen,
    setOpen,
    data,
    setData,
    action,
    setAction,
    refetch,
}: FormProps<BusinessUnit>) => {
    const { toast } = useToast();
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

    const [createBusinessUnit] = useCreateBusinessUnitMutation();
    const [updateBusinessUnit] = useUpdateBusinessUnitMutation();

    const { data: facilityCategoryData } = useGetCategoriesQuery(
        { org_id: orgId, query: "type=facility&only_parent=true" },
        { skip: !isOpen }
    );

    const { data: membershipGroupData } = useGetCategoriesQuery(
        { org_id: orgId, query: "type=membership" },
        { skip: !isOpen }
    );

    const { data: productCategoryData } = useGetCategoriesQuery(
        { org_id: orgId, query: "type=product" },
        { skip: !isOpen }
    );

    const { data: creditAddOnsData } = useGetCategoriesQuery(
        { org_id: orgId, query: "type=credit_add_ons" },
        { skip: !isOpen }
    );

    const categoryOptions = React.useMemo(() => {
        return Array.isArray(facilityCategoryData?.data)
            ? facilityCategoryData.data.map((cat) => ({
                value: String(cat.id),
                label: cat.name,
            }))
            : [];
    }, [facilityCategoryData]);

    const groupOptions = React.useMemo(() => {
        return Array.isArray(membershipGroupData?.data)
            ? membershipGroupData.data.map((cat) => ({
                value: String(cat.id),
                label: cat.name,
            }))
            : [];
    }, [membershipGroupData]);

    const productCategoryOptions = React.useMemo(() => {
        return Array.isArray(productCategoryData?.data)
            ? productCategoryData.data.map((cat) => ({
                value: String(cat.id),
                label: cat.name,
            }))
            : [];
    }, [productCategoryData]);

    const creditAddOnsOptions = React.useMemo(() => {
        return Array.isArray(creditAddOnsData?.data)
            ? creditAddOnsData.data.map((cat) => ({
                value: String(cat.id),
                label: cat.name,
            }))
            : [];
    }, [creditAddOnsData]);

    const form = useForm<BusinessUnit>({
        mode: "all",
        defaultValues: {
            name: '',
            facility_category_ids: [],
            membership_group_ids: [],
            product_category_ids: [],
            credit_add_ons_ids: [],
            org_id: orgId,
        },
    });

    const { handleSubmit, reset, formState: { isSubmitting } } = form;

    useEffect(() => {
    if (!isOpen) return;

    if (data && action === 'edit') {
        reset({
    id: data.id,
    name: data.name,
    facility_category_ids: (data.facility_category_ids ?? []).map(String),
    membership_group_ids: (data.membership_group_ids ?? []).map(String),
    product_category_ids: (data.product_category_ids ?? []).map(String),
    credit_add_ons_ids: (data.credit_add_ons_ids ?? []).map(String),
    org_id: orgId,
});
    } else {
        reset({
            name: '',
            facility_category_ids: [],
            membership_group_ids: [],
            product_category_ids: [],
            credit_add_ons_ids: [],
            org_id: orgId,
        });
    }
}, [data, action, isOpen, reset, orgId]);

    const onSubmit = async (formData: BusinessUnit) => {
        const payload = { ...formData, org_id: orgId };
        try {
            if (action === 'add') {
                const resp = await createBusinessUnit(payload);
                if (resp.error) {
                    const error = resp.error as FetchBaseQueryError & { data?: { message?: string } };
                    toast({ variant: 'destructive', title: 'Error', description: error.data?.message || 'Something went wrong.' });
                    return;
                }
                toast({ variant: 'success', title: 'Business Unit created successfully.' });
            } else {
                const resp = await updateBusinessUnit({ id: formData.id!, body: payload });
                if (resp.error) {
                    const error = resp.error as FetchBaseQueryError & { data?: { message?: string } };
                    toast({ variant: 'destructive', title: 'Error', description: error.data?.message || 'Something went wrong.' });
                    return;
                }
                toast({ variant: 'success', title: 'Business Unit updated successfully.' });
            }
            refetch();
            handleClose();
        } catch (error) {
            toast({ variant: 'destructive', title: 'Error', description: 'Something went wrong.' });
        }
    };

    const handleClose = () => {
        reset();
        setAction("add");
        setData(undefined);
        setOpen(false);
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>{action === 'add' ? 'Create' : 'Edit'} Business Unit</SheetTitle>
                    <SheetDescription>
                        <Separator className="my-2" />
                        <FormProvider {...form}>
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">

                                {/* Name */}
                                <Controller
                                    name="name"
                                    control={form.control}
                                    rules={{ required: 'Required' }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <FloatingLabelInput id="name" label="Business Unit Name" text="*" {...field} />
                                            {error && <p className="text-xs text-red-500">{error.message}</p>}
                                        </div>
                                    )}
                                />


                                <Controller
                                    name="facility_category_ids"
                                    control={form.control}
                                    render={({ field }) => (
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Facility Categories</label>
                                            <MultiSelect
                                                options={categoryOptions}
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select Categories"
                                            />
                                        </div>
                                    )}
                                />

                                
                                <Controller
                                    name="membership_group_ids"
                                    control={form.control}
                                    render={({ field }) => (
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Membership Groups</label>
                                            <MultiSelect
                                                options={groupOptions}
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select Groups"
                                            />
                                        </div>
                                    )}
                                />

                                
                                <Controller
                                    name="product_category_ids"
                                    control={form.control}
                                    render={({ field }) => (
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Product Categories</label>
                                            <MultiSelect
                                                options={productCategoryOptions}
                                                value={field.value ?? []}
                                                onChange={field.onChange}
                                                placeholder="Select Product Categories"
                                            />
                                        </div>
                                    )}
                                />

                                
                                <Controller
                                    name="credit_add_ons_ids"
                                    control={form.control}
                                    render={({ field }) => (
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">Additional Credits</label>
                                            <MultiSelect
                                                options={creditAddOnsOptions}
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select Additional Credits"
                                            />
                                        </div>
                                    )}
                                />

                                <LoadingButton
                                    type="submit"
                                    className="w-full bg-primary text-black"
                                    loading={isSubmitting}
                                >
                                    Save
                                </LoadingButton>
                            </form>
                        </FormProvider>
                    </SheetDescription>
                </SheetHeader>
            </SheetContent>
        </Sheet>
    );
};

export default BusinessUnitForm;